import { createTimeInstance } from '@utils/time';
import { PREVIEW_MAX_UNITS, SCOPE_OPTIONS, UNIVERSE_KINDS } from '@services/scoreSpecs';

// The selection half of the preview dock: what a preview run is pointed AT.
//
// Since fapi #492, `preview_score` no longer takes document ids. It takes the same body
// `preview_documents` takes (minus `limit`) and resolves the spec's own trigger over it — so the
// dock now describes a universe and a window, and the engine decides which documents that means.
//
// Everything here is pure so the body we put on the wire can be tested without mounting anything.
// That matters more than usual: nothing in this repo asserted the old request body, and the old
// one was wrong in a way no test could see.

// Re-exported, not restated. The cap is a wire fact and the service owns those (beside
// SCOPE_OPTIONS, UNIVERSE_KINDS and PREVIEW_STATUS); a second literal here is a second thing to
// forget when the server moves it. We do NOT enforce it — the request carries no `limit` any more,
// so this number only says the cap out loud and asks `preview_documents` for as many candidates as
// the paid run would score.
export const MAX_PREVIEW_UNITS = PREVIEW_MAX_UNITS;

// Index universes are deliberately absent. The index list and the ticker caps live only on
// `GET v2/genai/score_backfills/specs`, which is gated on the separate `score_backfill` mode; a
// 403 there is a global navigation away from the editor (services/api.js:62-77), so an operator
// with score_spec_admin and no backfill grant would be ejected by a background fetch. An empty
// dropdown would be worse than not offering the kind.
export const PREVIEW_UNIVERSE_KINDS = {
  WHOLE_CORPUS: UNIVERSE_KINDS.WHOLE_CORPUS,
  TICKERS: UNIVERSE_KINDS.TICKERS,
};

// Short spans only. The backfill's own presets top out at Max, which with no minDate resolves to a
// FIFTEEN year window (scoreBackfill/DateWindow.js:145) — one click from a paid run of up to 25
// model calls. A preview is for answering "does this prompt work", not for coverage.
export const PREVIEW_WINDOW_PRESETS = [
  { key: '1w', label: '1w', amount: 1, unit: 'week' },
  { key: '1m', label: '1m', amount: 1, unit: 'month' },
  { key: '3m', label: '3m', amount: 3, unit: 'month' },
  { key: '1y', label: '1y', amount: 1, unit: 'year' },
  { key: 'custom', label: 'Custom' },
];

// Tickers, not whole corpus, and one month, not one year: the first press of a button that spends
// real money should cost as little as it can while still being useful. An empty ticker list is a
// blocker (selectionProblem), so the default state cannot be run by accident at all.
export const defaultSelection = () => {
  const to = createTimeInstance().toDate();
  return {
    universeKind: PREVIEW_UNIVERSE_KINDS.TICKERS,
    tickerText: '',
    fromDate: createTimeInstance(to).subtract(1, 'month').toDate(),
    toDate: to,
    scope: SCOPE_OPTIONS.LATEST_PER_COMPANY,
  };
};

// Free text -> wire symbols. Order-stable so the operator's list reads back the way they typed it.
export const parseTickers = (text) => {
  const seen = new Set();
  return String(text || '')
    .split(/[\s,]+/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean)
    .filter((symbol) => {
      if (seen.has(symbol)) return false;
      seen.add(symbol);
      return true;
    });
};

// The wire wants a bare YYYY-MM-DD. A Date object left in the body does NOT fail loudly — the
// request sanitizer turns it into a full UTC ISO datetime (utils/jsonSafeForApi.js:27-29), which
// is off by a day for half the world. Always through here.
export const toISODate = (date) => (date ? createTimeInstance(date).format('YYYY-MM-DD') : null);

const buildUniverse = (selection) => {
  const kind = (selection || {}).universeKind;
  if (kind === PREVIEW_UNIVERSE_KINDS.TICKERS) {
    return { kind, value: parseTickers((selection || {}).tickerText) };
  }
  return { kind };
};

// One builder, two callers — the free resolve and the paid run must describe the SAME selection or
// the count the operator read means nothing. Mirrors scoreBackfill/ScoreBackfillForm.js's buildBody,
// MINUS market_cap_version: the spec-preview endpoints (preview_score / preview_documents) are not
// part of the nvstr-webapp#523 contract and take no such field. So once v2's table loads, a
// whole_corpus spec preview and a v2 backfill resolve against different company sets — a deliberate
// divergence, not drift. Revisit if those endpoints gain the field.
export const buildPreviewBody = (selection) => ({
  universe: buildUniverse(selection),
  from: toISODate((selection || {}).fromDate),
  to: toISODate((selection || {}).toDate),
  scope: (selection || {}).scope,
});

// `preview_documents` still takes a limit. We ask it for exactly as many candidates as a run would
// score, so its `truncated` answers the same question the run's does.
export const buildResolveBody = (selection) => ({
  ...buildPreviewBody(selection),
  limit: MAX_PREVIEW_UNITS,
});

// One sentence naming what is missing, or null when the selection is sendable. Rendered as the
// blocker on an aria-disabled button, per scoreBackfill/ScoreBackfillForm.js:411-425.
export const selectionProblem = (selection) => {
  const { universeKind, fromDate, toDate } = selection || {};
  if (universeKind === PREVIEW_UNIVERSE_KINDS.TICKERS && parseTickers((selection || {}).tickerText).length === 0) {
    return 'Name at least one ticker, or switch to the whole corpus.';
  }
  if (!fromDate || !toDate) return 'Set both ends of the window.';
  if (createTimeInstance(fromDate).isAfter(createTimeInstance(toDate))) {
    return 'The window starts after it ends.';
  }
  return null;
};
